import {ReactNode} from 'react';

export const defPagination = {
  count: 0,
  current_page: 1,
  last_page_url: '',
  next_page_url: '',
  per_page: 1,
  prev_page_url: '',
  total: 1,
  total_pages: 1,
};
// pagination
export interface IPagination {
  count: number;
  current_page: number;
  last_page_url: string;
  next_page_url?: string;
  per_page: number;
  prev_page_url?: string;
  total: number;
  total_pages: number;
}
// request
export interface IGetRequest {
  status_code: number;
  errors: any[];
  data: any[];
}

// file
export interface IFile {
  id: number;
  path: string;
  full_path?: string;
  mim: string;
  width: number;
  height: number;
  size: number;
  type: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  is_external_links?: 1 | 0;
}

// chapter
export interface IChapter {
  id: number;
  file?: IFile;
  file_id?: number;
  title: string;
  desc?: string;
  slug: string;
  subjects_id: number;
  subject: ISubject;
  updated_at: string;
  created_at: string;
}
export interface INewChapter {
  file_id?: number;
  title: string;
  desc?: string;
  subjects_id: number;
}
export interface IGetChapters {
  result: IChapter[];
  status: boolean;
  status_code: number;
  message: string;
}

export interface IGetChapter {
  result: IChapter;
  status: boolean;
  status_code: number;
  message: string;
}

// quiz-type
export interface IQuizType {
  id: number;
  title: string;
  children?: IQuizType[];
}
export interface IGetQuizTypes {
  result: {data: IQuizType[]; pagination: IPagination};
  status: boolean;
  status_code: number;
  message: string;
}

type TOption = {id: number; text: string; is_correct?: 0 | 1; sort?: number};

export interface IQuestion {
  id: number;
  title: string;
  slug: string;
  level: number;
  level_title: 'difficult' | 'easy' | 'very difficult' | 'very easy';
  description?: string;

  hint: string | null;
  answer_additional_info?: string | null;

  quiz_types: IQuizType[];
  chapter?: IChapter;
  lesson?: ILesson;
  created_at: string;
  updated_at: string;
  file: any | null;
  options: TOption[];
  challenge?: IChallenge;

  file_id: string | null;
  answer_file_id: string | null;
  hint_file_id: string | null;
  answer_additional_info_file_id?: string | null;

  picture: IFile | null;
  answer_picture: null | IFile;
  hint_picture?: null | IFile;
  answer_additional_info_picture?: null | IFile;
}

export interface IQuestionsData {
  data: IQuestion[];
  pagination: IPagination;
}
export interface IUsersData {
  data: IUser[];
  pagination: IPagination;
}

export interface IGetQuestions {
  result: IQuestionsData;
  status: boolean;
  status_code: number;
  message: string;
}
export interface IGetQuestion {
  result: IQuestion;
  status: boolean;
  status_code: number;
  message: string;
}

export type TQuizType =
  | 'one-word'
  | 'true-false'
  | 'multiple-choice'
  | 'question-with-picture'
  | 'reflexion'
  | 'self-evaluate';

export enum QUESTION_TYPES {
  ONE_WORD = 'one-word',
  TRUE_FALSE = 'true-false',
  MULTIPLE_CHOICE = 'multiple-choice',
  WITH_PICTURE = 'question-with-picture',
  SELF_EVALUATE = 'self-evaluate',
  REFLEXION = 'reflexion',
}

type TQuestionType = {
  main_quiz_type: 'les-quiz';
  quiz_type: TQuizType;
};

type TQuestionBody = {
  title: string;
  description: string;
  level: number;
  chapter_id: number;
  file_id?: number;
  type: TQuestionType;
};

export interface ITrueFalseQuestion {
  question: TQuestionBody;
  options_data: {
    question_answer: true;
  };
}

export interface IOneWordQuestion {
  question: TQuestionBody;
  options_data: {one_word_question_answers: string[]};
}

export interface ISelfEvaluateQuestion {
  question: TQuestionBody;
  options_data: {
    self_evaluate_question_text: string;
  };
}

type TMultipleChoiceOption = [string, boolean];

export interface IMultipleChoiceQuestion {
  question: TQuestionBody;
  options_data: {
    question_options: TMultipleChoiceOption[];
  };
}

type TWithPictureOption = [string, number];
export interface IWithPictureQuestion {
  question: TQuestionBody;
  options_data: {
    question_options_pictured: TWithPictureOption[];
  };
}

export type TNewQuestion =
  | ITrueFalseQuestion
  | IOneWordQuestion
  | IMultipleChoiceQuestion
  | ISelfEvaluateQuestion
  | IWithPictureQuestion;

// challenges
export type IChallenge = IChallengeMonth | IChallengeSeason;
export interface IChallengeSeason {
  id: number;
  title: string;
  desc: string;
  type: 'season';
  address: string;
  email: string;
  grades_id: number;
  published: 0 | 1;
  expire_at: Date;
  created_at: Date;
  updated_at: Date;
  question: string;
}
export interface IChallengeMonth {
  id: number;
  title: string;
  desc: string;
  type: 'month';
  grades_id: number;
  published: 0 | 1;
  expire_at: Date;
  created_at: Date;
  updated_at: Date;
  address: string;
  email: string;
  question: string;
}
export interface TNewChallenge {
  title: string;
  desc: string;
  type: 'month' | 'season';
  grades_id: number;
  published: 0 | 1;
  expire_at: Date;
  address: string;
  email: string;
  question: string;
}

export interface IGetChallenge extends IGetRequest {
  result: IChallenge;
}

export interface IGetChallenges extends IGetRequest {
  result: IChallenge[];
}

// challenge results
export interface IChallengeResult {
  id: number;
  title: string;
  sub_title: string;
  desc: string;
  author: string;
  expire_at: Date;
  published_at: Date;
  challenge: IChallenge;
  files: IFile[];
}
export interface INewChallengeResult {
  title: string;
  sub_title: string;
  desc: string;
  days_valid_after_challenge_expires: number;
  challenge_id: number;
  author: string;
  file_ids: number[];
}

export interface IGetChallengeResult extends IGetRequest {
  result: IChallengeResult;
}

export interface IGetChallengeResults extends IGetRequest {
  result: IChallengeResult[];
}

// subject
export interface IIcon {
  id: number;
  color: string;
  subjects_id: number;
  file_id: number;
  created_at: Date;
  updated_at: Date;
}
export interface ISubject {
  id: number;
  title: string;
  slug: string;
  grades_id: number;
  grade?: number;
  created_at: string;
  updated_at: string;
  specialty: 0 | 1;
  icon?: IIcon;
}

export interface ISubjectData {
  grades: IGrade[];
  specialties: ISpecialty[];
  subjects: ISubject[];
}

export interface IGetSettings {
  result: ISubjectData;
  status: boolean;
  status_code: number;
  message: string;
}

export enum ContentStatusTypes {
  fiches_content_status = 'fiches_content_status',
  lexique_content_status = 'lexique_content_status',
  mind_map_status = 'mind_map_status',
  video_methodology_status = 'video_methodology_status',
  video_programme_status = 'video_programme_status',
  audio_status = 'audio_status',
  video_exercise_status = 'video_exercise_status',
  reflexion_status = 'reflexion_status',
  lesquiz_status = 'lesquiz_status',
  lesquiz_intensif_status = 'lesquiz_intensif_status',
  lesquiz_reversion_status = 'lesquiz_reversion_status',
  lesquiz_chrono_status = 'lesquiz_chrono_status',
}

export interface ISubjectV2 {
  id: number;
  title: string;
  slug: string;
  file_adress: string | null;
  is_specialty: number;
  is_free: number;
  payment_enabled: number;
  color_code: string;
  grade: IGrade;
  status: 'active' | 'in-active';
  icon: IIcon | null;
  type_limits: {chrono_limit: number; intensif_limit: number};
  content_statuses: {
    [key in ContentStatusTypes]?: 'active' | 'in-active';
  };
}

export interface IGetSubjects {
  result: { data:ISubjectV2[] };
  status: boolean;
  status_code: number;
  message: string;
}

export interface IGetSubject {
  result: ISubjectV2;
  status: boolean;
  status_code: number;
  message: string;
}

export interface NewSubjectStatus {
  status?: 'active' | 'in-active';
  content_statuses?: {
    [key in ContentStatusTypes]?: 'active' | 'in-active';
  };
}

// grade
export interface IGrade {
  id: number;
  title: string;
  slug: string;
  created_at: string;
  updated_at: string;
}

// speciality
export interface ISpecialty {
  id: number;
  grades_id: number;
  title: string;
  slug: string;
  created_at: string;
  updated_at: string;
  specialty: 0 | 1;
}

// react
export type WithChildren = {
  children?: ReactNode;
};

// lesson category
export interface IGetLessonCategories {
  result: ILessonCategoriesData;
  status: boolean;
  status_code: number;
  message: string;
}

export interface ILessonCategoriesData {
  data: ILessonCategory[];
  pagination: IPagination | {};
}

export interface ILessonCategory {
  id: number;
  title: string;
  subject: {
    file_adress: null | string;
    grade: number;
    icon: null | number;
    id: number;
    slug: string;
    title: string;
  };
  lessons: any[];
}

// lessons
export interface ILessonsData {
  data: ILesson[];
  pagination: IPagination;
}

export interface IGetLessons {
  result: ILessonsData;
  status: boolean;
  status_code: number;
  message: string;
}
export interface IGetLesson {
  result: ILesson;
  status: boolean;
  status_code: number;
  message: string;
}

export interface ILesson {
  id: 1;
  title: string;
  type: ILessonType;
  user_data?: any;
  media: IFile;
  thumbnail?: IFile;
  image?: IFile;
  description?: string;
  media_duration: number;
  category?: {
    id: number;
    title: string;
    subject_id: number;
  };
  subject: {
    id: number;
    title: string;
    slug: string;
    icon?: any;
    file_adress?: string;
    grade: number;
  };
  miniQuizez?: IQuestion[];
  mediaSections?: ILessonSection[];
}

type ILessonType =
  | 'audio'
  | 'video-methodology'
  | 'video-programmeng'
  | 'video-exercise'
  // | "reflexion"
  | 'video-programme';
export interface INewLesson {
  type: ILessonType;
  title: string;
  file?: number;
  subject_id: number;
  duplicate_id?: number;
  category_id?: number;
  thumbnail?: number; //when type is video-methodology or video-programme required
  image?: number; //when type is video-methodology or video-programme required
  duration?: number; //seconds
  file_url?: string;
  description?: string;
}

// lesson section
export interface INEWLessonSection {
  title: string;
  minutes: number;
  seconds: number;
  description: string;
}

export interface ILessonSection {
  id: number;
  lesson_id: number;
  lesson: any;
  title: string;
  minutes: number;
  seconds: number;
  description: string;
}

export interface IGetLessonSection {
  result: ILessonSection;
  status: boolean;
  status_code: number;
  message: string;
}

export interface ILessonSectionsData {
  data: ILessonSection[];
  pagination: IPagination | {};
}

export interface IGetLessonSections {
  result: ILessonSectionsData;
  status: boolean;
  status_code: number;
  message: string;
}

// lexique
export interface ILexique {
  id: number;
  title: string;
  desc: string;
  subjects: ISubject[];
}
export interface INewLexique {
  title: string;
  desc: string;
  subject_ids: number[];
}

export interface IGetLexiques {
  result: ILexique[];
  status: boolean;
  status_code: number;
  message: string;
}
export interface IGetLexique {
  result: ILexique;
  status: boolean;
  status_code: number;
  message: string;
}

// fiches
export interface IFiche {
  id: number;
  title: string;
  slug: string;
  desc: string;
  created_at: string;
  updated_at: string;
  sub_title: string;
  author: string;
  files: IFile[];
  subjects: ISubject[];
}
export interface INewIFiche {
  title: string;
  sub_title: string;
  desc: string;
  author: string;
  subject_ids: number[];
  file_ids: number[];
}

export interface IGetIFiches {
  result: IFiche[];
  status: boolean;
  status_code: number;
  message: string;
}
export interface IGetIFiche {
  result: IFiche;
  status: boolean;
  status_code: number;
  message: string;
}

// objects
export enum ObjectTypes {
  avatar = 'avatar',
  accessory = 'accessory',
  badge = 'badge',
  special_object = 'special_object',
}

export type TObject = (IAvatar | IAccesory | ISpecialObject | IBadge) & {objectType: ObjectTypes};

export type TNewObject = (INewAccesory | INewAvatar | INewBadge | INewSpecialObject) & {
  objectType: ObjectTypes;
};
export type TGetObject = IGetIAccesory | IGetIAvatar | IGetBadge | IGetSpecialObject;

// objects, avatars
export interface IGetIAvatars extends IGetRequest {
  result: IAvatar[];
}

export interface IGetIAvatar extends IGetRequest {
  result: IAvatar;
}

export interface IAvatar {
  id: number;
  title: string;
  desc: string;
  price: number;
  created_at: string;
  updated_at: string;
  file: IFile;
}

export interface INewAvatar {
  title: string;
  desc: string;
  price: number;
  file_id: number;
}

// objects, accessories
export interface IGetIAccesories extends IGetRequest {
  result: IAccesory[];
}

export interface IGetIAccesory extends IGetRequest {
  result: IAccesory;
}

export interface IGetIGrades extends IGetRequest {
  result: IGrades[];
}
export interface IGetISubjectMenus extends IGetRequest {
  result: IMenus[];
}
export interface IGetActKeys extends IGetRequest {
  result: {
    data:IActKey[],
    pagination: IPagination;
  };
}
export interface IGetAllUsers extends IGetRequest {
  result: {
    data:IAllUsers[],
    pagination: IPagination;
  };
}
export interface IAllUsers             {
  id: number,
  email: string,
  username:string,
  note: string,
  specialties_id: [
    6,
    7
  ],
  specialty: [
    {
      id: number,
      title: string,
      slug: string,
      status: "in-active" |"active",
      specialty: number,
      grades_id: number,
      color_code: null,
      payment_enabled: number,
      is_free: number,
      fiches_content_status: "in-active" | "active",
      lexique_content_status: "in-active" | "active",
      mind_map_status: "in-active" | "active",
      video_methodology_status: "in-active" | "active",
      video_programme_status: "in-active" | "active",
      audio_status: "in-active" | "active",
      created_at: string,
      updated_at: string,
      video_exercise_status: "in-active" | "active",
      lesquiz_status: "in-active" | "active",
      lesquiz_reversion_status: "in-active" | "active",
      lesquiz_intensif_status: "in-active" | "active",
      lesquiz_chrono_status: "in-active" | "active",
      reflexion_status: "in-active" | "active",
      deleted_at: null,
      type_limits: {
        chrono_limit: number,
        intensif_limit: number
      },
      pivot: {
        user_id: number,
        subjects_id: number
      }
    },
    {
      id: number,
      title: string,
      slug: string,
      status: "in-active" |"active",
      specialty: number,
      grades_id: number,
      color_code: null,
      payment_enabled: number,
      is_free: number,
      fiches_content_status: "in-active" | "active",
      lexique_content_status: "in-active" | "active",
      mind_map_status: "in-active" | "active",
      video_methodology_status: "in-active" | "active",
      video_programme_status: "in-active" | "active",
      audio_status: "in-active" | "active",
      created_at: string,
      updated_at: string,
      video_exercise_status: "in-active" | "active",
      lesquiz_status: "in-active" | "active",
      lesquiz_reversion_status: "in-active" | "active",
      lesquiz_intensif_status: "in-active" | "active",
      lesquiz_chrono_status: "in-active" | "active",
      reflexion_status: "in-active" | "active",
      deleted_at: null,
      type_limits: {
        chrono_limit: number,
        intensif_limit: number
      },
      pivot: {
        user_id: number,
        subjects_id: number
      }
    }
  ],
  level: number,
  points: number,
  keys: number,
  grades_id: number,
  grade: {
    id: number,
    title: string,
    slug: string,
    specialties_id: null,
    specialty: null,
    status: "active" | "in-active",
    payment_enabled: number,
    speciality_limit: number,
    shops: [
      {
        id: number,
        title: string,
        desc: null,
        type: string,
        user_id: number,
        created_at: string,
        updated_at: string,
        pivot: {
          shop_id: number,
          shoppable_id: number,
          shoppable_type: string
        }
      }
    ],
    created_at: string,
    updated_at: string
  },
  avatar: {
  id: number,
  title: string,
    desc: string,
    price: number,
    file: {
      id: number,
      mim: string,
      width: number,
      height: number,
      size: number,
      path: string,
      full_path: string,
      type: string,
      is_external_links: number,
      created_at: string,
      updated_at: string
    },
    created_at: string
  }
}
export interface IActKeyBody {
  title?: string,
  keys_count?: number,
  content_id?: string,
  content_type?: string,
  duration?: string,
}
export interface IExportActKeys {
  key_ids: number[]
}
export interface IActKey {
  assigned_to: string,
  assigned_user: {
    accessory:any
    accessory_x_position:any
    accessory_y_position:any
    avatar:string
    email:string
    grade:string
    grades_id:string
    invitation_code:string
    is_admin:number
    keys:number
    level:number
    points:number
    specialties_id:[]
    specialty:[]
    username:string
    level_details: {
      next_level_needed_points: number,
      progress_to_next_level: number,
      progress_to_next_level_percentage: number,
    }
    badges:{
      badge_user_info:{
        access:boolean
        leveled:boolean
      }
      desc:string
      has_chosen:number
      id:number
      order:number
      title:string
      type:string
      file:{
        gray: {
          created_at:string
          full_path:string
          height:number
          id:number
          is_external_links:number
          mim:string
          path:string
          size:number
          type:string
          updated_at:string
          width:number
        }
        normal:{
          created_at:string
          full_path:string
          height:number
          id:number
          is_external_links:number
          mim:string
          path:string
          size:number
          type:string
          updated_at:string
          width:number
        }
      }
    }[]
  },
  id: number,
  key: string,
  record_id: number,
  status: string,
  used_at: string,
  used_by: string,
  collection: {
    content_id: number,
    content_type: string,
    created_at: string,
    duration: string,
    id: number,
    keys_count: number,
    keys_used: number,
    title: string,
    updated_at: string,
    content: {
      created_at: string,
      id: number,
      payment_enabled: number,
      slug: string,
      specialties_id: string,
      status: string,
      title: string,
      updated_at: string,
    }
  }
}
export interface IMenus {
  id: number;
  title: string;
  slug: string;
  file_id: string;
  type: string;
  status: "in-active" | "active"
}
export interface IGrades {
  created_at:string;
  id:number;
  slug:string;
  status: 'active' | 'in-active',
  payment_enabled: 0 | 1,
  specialties_id:number;
  title:string;
  updated_at:string;
}
export interface IAccesory {
  id: number;
  title: string;
  desc: string;
  price: number;
  created_at: string;
  updated_at: string;
  file: IFile;
}

export interface INewAccesory {
  title: string;
  desc: string;
  price: number;
  file_id: number;
}

// objects, special objects
export interface IGetSpecialObjects extends IGetRequest {
  result: ISpecialObject[];
}

export interface IGetSpecialObject extends IGetRequest {
  result: ISpecialObject;
}

export interface ISpecialObject {
  id: number;
  title: string;
  desc: string;
  price: number;
  created_at: string;
  updated_at: string;
  file: IFile;
  type: string;
}

export interface INewSpecialObject {
  title: string;
  desc: string;
  price: number;
  file_id: number;
  type: string;
}

export interface IGetSpecialObjectTypes extends IGetRequest {
  result: string[];
}

// badges
export interface IGetBadges extends IGetRequest {
  result: IBadge[];
}

export interface IGetBadge extends IGetRequest {
  result: IBadge;
}

export interface IGetBadgeTypes extends IGetRequest {
  result: {[key: string]: {translation: string; is_leveled: boolean}};
}

export interface IBadge {
  id: number;
  title: string;
  desc: string;
  created_at: string;
  updated_at: string;
  file: IFile;
  files: IFile[];
  type: string;
}

export interface INewBadge {
  title: string;
  file_id: number;
  type: string;
}

// subscription
export interface ISubscription {
  id: number;
  title: string;
  desc: string;
  price_per_grade: number;
  price_per_subject: number;
  subject_info: string;
  grade_info: string;
  img: null | number;
  type: string;
  amount: 1 | 3 | 12;
  created_at: string;
  updated_at: string;
}

export interface IGetSubscription {
  result: ISubscription;
  message: string;
  status: boolean;
  status_code: number;
  errors: any[];
  data: any[];
}

export interface IGetSubscriptions {
  result: ISubscription[];
  message: string;
  status: boolean;
  status_code: number;
  errors: any[];
  data: any[];
}

export interface INewSubscription {
  id?: number;
  title: string;
  desc: string;
  price_per_grade: number;
  price_per_subject: number;
  subject_info: string;
  grade_info: string;
  type: 'month';
  amount: 1 | 3 | 12;
}

// mindmaps
export interface IMindmapChild {
  id: number;
  parent_id: number;
  subjects_id: null;
  title: string;
  is_final: 0 | 1;
  description: string;
  created_at?: string;
  updated_at?: string;
  parent?: IMindMap | IMindmapChild;
  children_recursive: IMindmapChild[];
}

export interface IMindMap {
  id: number;
  parent_id: null;
  title: string;
  subjects_id: number;
  description: null;
  is_final: 0;
  created_at: string;
  updated_at: string;
  children: IMindmapChild[];
}

export type TNewMindmapChildren = [
  //  l1
  {title: string; id: 2; parent_id: 1; is_final: 0},
  {title: string; id: 3; parent_id: 1; is_final: 0},
  {title: string; id: 4; parent_id: 1; is_final: 0},
  //  l2
  {title: string; id: 5; parent_id: 2; is_final: 0},
  {title: string; id: 6; parent_id: 2; is_final: 0},
  {title: string; id: 7; parent_id: 2; is_final: 0},
  {title: string; id: 8; parent_id: 3; is_final: 0},
  {title: string; id: 9; parent_id: 3; is_final: 0},
  {title: string; id: 10; parent_id: 3; is_final: 0},
  {title: string; id: 11; parent_id: 4; is_final: 0},
  {title: string; id: 12; parent_id: 4; is_final: 0},
  {title: string; id: 13; parent_id: 4; is_final: 0},
  //  l3
  {title: string; id: 14; parent_id: 5; is_final: 1; description?: string},
  {title: string; id: 15; parent_id: 5; is_final: 1; description?: string},
  {title: string; id: 16; parent_id: 5; is_final: 1; description?: string},
  {title: string; id: 17; parent_id: 6; is_final: 1; description?: string},
  {title: string; id: 18; parent_id: 6; is_final: 1; description?: string},
  {title: string; id: 19; parent_id: 6; is_final: 1; description?: string},
  {title: string; id: 20; parent_id: 7; is_final: 1; description?: string},
  {title: string; id: 21; parent_id: 7; is_final: 1; description?: string},
  {title: string; id: 22; parent_id: 7; is_final: 1; description?: string},
  //
  {title: string; id: 23; parent_id: 8; is_final: 1; description?: string},
  {title: string; id: 24; parent_id: 8; is_final: 1; description?: string},
  {title: string; id: 25; parent_id: 8; is_final: 1; description?: string},
  {title: string; id: 26; parent_id: 9; is_final: 1; description?: string},
  {title: string; id: 27; parent_id: 9; is_final: 1; description?: string},
  {title: string; id: 28; parent_id: 9; is_final: 1; description?: string},
  {title: string; id: 29; parent_id: 10; is_final: 1; description?: string},
  {title: string; id: 30; parent_id: 10; is_final: 1; description?: string},
  {title: string; id: 31; parent_id: 10; is_final: 1; description?: string},
  //
  {title: string; id: 32; parent_id: 11; is_final: 1; description?: string},
  {title: string; id: 33; parent_id: 11; is_final: 1; description?: string},
  {title: string; id: 34; parent_id: 11; is_final: 1; description?: string},
  {title: string; id: 35; parent_id: 12; is_final: 1; description?: string},
  {title: string; id: 36; parent_id: 12; is_final: 1; description?: string},
  {title: string; id: 37; parent_id: 12; is_final: 1; description?: string},
  {title: string; id: 38; parent_id: 13; is_final: 1; description?: string},
  {title: string; id: 39; parent_id: 13; is_final: 1; description?: string},
  {title: string; id: 40; parent_id: 13; is_final: 1; description?: string}
];

export interface INewMindmap {
  title: string;
  subject_id: number;
  id: 1;
  parent_id: null;
  is_final: 0;
  children: TNewMindmapChildren;
}

export interface IGetMindmap {
  result: IMindMap;
  message: string;
  status: boolean;
  status_code: number;
  errors: any[];
  data: any[];
}

export interface IGetMindmaps {
  result: IMindMap[];
  message: string;
  status: boolean;
  status_code: number;
  errors: any[];
  data: any[];
}

export const newMindMapTemplate: INewMindmap = {
  title: '',
  subject_id: 0,
  id: 1,
  parent_id: null,
  is_final: 0,
  children: [
    //  l1
    {title: '', id: 2, parent_id: 1, is_final: 0},
    {title: '', id: 3, parent_id: 1, is_final: 0},
    {title: '', id: 4, parent_id: 1, is_final: 0},
    //  l2
    {title: '', id: 5, parent_id: 2, is_final: 0},
    {title: '', id: 6, parent_id: 2, is_final: 0},
    {title: '', id: 7, parent_id: 2, is_final: 0},
    {title: '', id: 8, parent_id: 3, is_final: 0},
    {title: '', id: 9, parent_id: 3, is_final: 0},
    {title: '', id: 10, parent_id: 3, is_final: 0},
    {title: '', id: 11, parent_id: 4, is_final: 0},
    {title: '', id: 12, parent_id: 4, is_final: 0},
    {title: '', id: 13, parent_id: 4, is_final: 0},
    //  l3
    {title: '', id: 14, parent_id: 5, is_final: 1, description: ''},
    {title: '', id: 15, parent_id: 5, is_final: 1, description: ''},
    {title: '', id: 16, parent_id: 5, is_final: 1, description: ''},
    {title: '', id: 17, parent_id: 6, is_final: 1, description: ''},
    {title: '', id: 18, parent_id: 6, is_final: 1, description: ''},
    {title: '', id: 19, parent_id: 6, is_final: 1, description: ''},
    {title: '', id: 20, parent_id: 7, is_final: 1, description: ''},
    {title: '', id: 21, parent_id: 7, is_final: 1, description: ''},
    {title: '', id: 22, parent_id: 7, is_final: 1, description: ''},
    //
    {title: '', id: 23, parent_id: 8, is_final: 1, description: ''},
    {title: '', id: 24, parent_id: 8, is_final: 1, description: ''},
    {title: '', id: 25, parent_id: 8, is_final: 1, description: ''},
    {title: '', id: 26, parent_id: 9, is_final: 1, description: ''},
    {title: '', id: 27, parent_id: 9, is_final: 1, description: ''},
    {title: '', id: 28, parent_id: 9, is_final: 1, description: ''},
    {title: '', id: 29, parent_id: 10, is_final: 1, description: ''},
    {title: '', id: 30, parent_id: 10, is_final: 1, description: ''},
    {title: '', id: 31, parent_id: 10, is_final: 1, description: ''},
    //
    {title: '', id: 32, parent_id: 11, is_final: 1, description: ''},
    {title: '', id: 33, parent_id: 11, is_final: 1, description: ''},
    {title: '', id: 34, parent_id: 11, is_final: 1, description: ''},
    {title: '', id: 35, parent_id: 12, is_final: 1, description: ''},
    {title: '', id: 36, parent_id: 12, is_final: 1, description: ''},
    {title: '', id: 37, parent_id: 12, is_final: 1, description: ''},
    {title: '', id: 38, parent_id: 13, is_final: 1, description: ''},
    {title: '', id: 39, parent_id: 13, is_final: 1, description: ''},
    {title: '', id: 40, parent_id: 13, is_final: 1, description: ''},
  ],
};

export const mindmapChildrenMapIds = [
  {
    id: 0,
    parent_id: -1,
  },
  {
    id: 1,
    parent_id: -1,
  },
  {
    id: 2,
    parent_id: -1,
  },
  {
    id: 3,
    parent_id: 0,
  },
  {
    id: 4,
    parent_id: 0,
  },
  {
    id: 5,
    parent_id: 0,
  },
  {
    id: 6,
    parent_id: 1,
  },
  {
    id: 7,
    parent_id: 1,
  },
  {
    id: 8,
    parent_id: 1,
  },
  {
    id: 9,
    parent_id: 2,
  },
  {
    id: 10,
    parent_id: 2,
  },
  {
    id: 11,
    parent_id: 2,
  },
  {
    id: 12,
    parent_id: 3,
  },
  {
    id: 13,
    parent_id: 3,
  },
  {
    id: 14,
    parent_id: 3,
  },
  {
    id: 15,
    parent_id: 4,
  },
  {
    id: 16,
    parent_id: 4,
  },
  {
    id: 17,
    parent_id: 4,
  },
  {
    id: 18,
    parent_id: 5,
  },
  {
    id: 19,
    parent_id: 5,
  },
  {
    id: 20,
    parent_id: 5,
  },
  {
    id: 21,
    parent_id: 6,
  },
  {
    id: 22,
    parent_id: 6,
  },
  {
    id: 23,
    parent_id: 6,
  },
  {
    id: 24,
    parent_id: 7,
  },
  {
    id: 25,
    parent_id: 7,
  },
  {
    id: 26,
    parent_id: 7,
  },
  {
    id: 27,
    parent_id: 8,
  },
  {
    id: 28,
    parent_id: 8,
  },
  {
    id: 29,
    parent_id: 8,
  },
  {
    id: 30,
    parent_id: 9,
  },
  {
    id: 31,
    parent_id: 9,
  },
  {
    id: 32,
    parent_id: 9,
  },
  {
    id: 33,
    parent_id: 10,
  },
  {
    id: 34,
    parent_id: 10,
  },
  {
    id: 35,
    parent_id: 10,
  },
  {
    id: 36,
    parent_id: 11,
  },
  {
    id: 37,
    parent_id: 11,
  },
  {
    id: 38,
    parent_id: 11,
  },
];

// FAQs
export interface IFAQ {
  id: number;
  title: string;
  description: string;
}

export interface INewFAQ {
  title: string;
  description: string;
}

export interface IGetFAQs {
  result: IFAQ[];
  status: boolean;
  status_code: number;
  message: string;
}

export interface IGetFAQ {
  result: IFAQ;
  status: boolean;
  status_code: number;
  message: string;
}

// admins
export interface IGetAdmin extends IGetRequest {
  result: IAdmin;
}
export interface IGetAdmins extends IGetRequest {
  result: IAdmin[];
}
export interface IAdmin {
  id: number;
  email: string;
  username: string;
  is_admin: 1 | 0;
}

export interface INewAdmin {
  email: string;
  username: string;
  password: string;
  password_confirmation: string;
}

// users
export interface IUser {
  id: number;
  email: string;
  username: string;
  specialties_id: number[];
  specialty: ISpecialty[];
  level: number;
  points: number;
  keys: number;
  grades_id: number;
  grade: IGrade;
  avatar: IAvatar | null;
  note: string | null;
  first_name: string | null;
  last_name: string | null;
  address: string | null;
}

export interface IGetUser extends IGetRequest {
  result: IUser;
}

export interface IGetUsers extends IGetRequest {
  result: IUsersData;
}
// reviews
export interface IGetReviews extends IGetRequest {
  result: IReviewsData;
}
export interface IReviewsData {
  data: IReview[];
  pagination: IPagination;
}
export interface IReview {
  id: number;
  user_id: number;
  review: string;
  created_at: string;
  updated_at: string;
}
// question report
export interface IGetQuestionReport extends IGetRequest {
  result: IQuestionReportData;
}
export interface IQuestionReportData {
  data: IQuestionReport[];
  pagination: IPagination;
}
export interface IQuestionReport {
  id: number;
  question_id: number;
  status: string;
  updated_at: string;
  created_at: string;
  question: {
    id: number;
    question_id: string;
    title: string;
    slug: string;
    level: number;
    level_title: string;
    description?: string | null;
    hint_picture?: string | null;
    hint: string | null;
    answer_additional_info_picture:string | null;
    answer_additional_info: string | null;
    quiz_types:IQuizType[];
    options:string | null;
    picture:string | null;
    answer_picture:string | null;
    chapter: string | null;
  };
  reports: [
    {
      id: number;
      question_id: number;
      comment: string;
      updated_at: string;
      created_at: string;
    }
  ];
}
// user subscription
export interface IUserSubscription {
  started_at: string;
  expire_at: string;
  payment_id: number;
  payment_type: string;
  purchase_key_type: string;
  subjects: ISubject[];
  type: string;
}
export interface IGetUserSubscriptions extends IGetRequest {
  result: IUserSubscription[];
}

export interface IPutGrade {
  status: "in-active" | "active",
  payment_enabled?: boolean
}

export interface IPutAssignToUser {
  assigned_to: number
}

export type ActKeyQueriesTypes = {
  'filters[record_id]' ?: number,
  'filters[record.content_type]' ?: 'grade' | 'subject',
  'filters[record.content_id]' ?: string,
  'filters[record.duration]' ?: '1-week'|'1-month'|'3-months'|'1-year' | string,
  'filters[status]'?: 'used'|'not-used'| string,
  'search'?: string,
  'order[column]'?: number,
  'order[dir]' ?: 'asc' | 'desc',
  page?: number
}

export type AllUsersQueriesTypes = {
  per_page?: number,
  page?: number,
  search?: string
}

export interface aggregateReportResponse {
  result: {
    data: QuestionItem[];
    pagination: IPagination;
  };
  message: string;
  status: boolean;
  status_code: number;
  errors: any[]; // if errors always array of strings or objects, you can refine
  data: any[];   // seems unused but present in schema
}

export interface QuestionItem {
  id: number;
  question_id: number;
  aggregate_id: number;
  comment: string;
  exam: Exam;
  answers: Answer[];
  user:{username:string};
  updated_at: string; // ISO datetime string
  created_at: string; // ISO datetime string
}

export interface Exam {
  id: number;
  mode: string;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface Answer {
  id: number;
  question_id: number;
  text: string;
  system_checked_result: number;
  user_self_evaluate_result: number;
  final_result: number;
  created_at: string;
  updated_at: string;
}

export interface IUserMetrics {
  total_registered_users: number,
  active_users: {
    count: number,
    from: string,
    to: string,
  },
  new_sign_ups: {
    count: number,
    from: string,
    to: string,
  },
  daily_active_users: {
    count: number,
    day: string,
  },
  monthly_active_users: {
    count: number,
    month: string,
  }
}
export interface IGetUserMetrics extends IGetRequest {
  result: IUserMetrics;
}
export type userMetricParamsType = {
  active_users_from?: string
  active_users_to?: string
}
export type userActivitiesParamsType = {
  activity_per_user_deciles_from?: string
  activity_per_user_deciles_to?: string
  top_active_users_from?: string
  top_active_users_to?: string
}
export type userContentsParamsType = {
  from?: string
  to?: string
}
export interface IUserContents {
  most_popular_activity_type: {
    type: string,
    activity_count: number,
  },
  least_popular_activity_type: {
    type: string,
    activity_count: number,
  },
  popular_content_by_activity_type:{
      type: string,
      event_type: string,
      content: {
        entity_type: string,
        entity: {
          id: number,
          title: string,
        },
        activity_count: number,
      }
    }[],
  most_popular_grade: {
    grade_id: number,
    grade: {
      id: number,
      title: string,
      slug: string,
    },
    count: number,
  },
  most_popular_subject:{
      event_type: string,
      data: {
        subject_id: number,
        subject: {
          id: number,
          title: string,
          slug: string,
        },
        count: number,
      }
    }[]
}
export interface IGetUserContent extends IGetRequest {
  result: IUserContents;
}

export interface IUserActivities {
  activity_per_user: {
    count: number,
    month: string,
  },
  top_active_users: {
    data:{
        id: number,
        username: string,
        email: string,
        activity_count: number,
      }[],
    from: string,
    to: string,
  },
  activity_per_user_deciles: {
      decile: number,
      user_count: number,
      average_activity_count: number,
    }[]
}
export interface IGetUserActivity extends IGetRequest {
  result: IUserActivities;
}
export interface Avatar {
  id: number;
  title: string;
  has_animation: boolean;
  animation_file_link: string;
}

export interface Background {
  exists: boolean;
  url: string;
}

export interface Audio {
  exists: boolean;
  url: string;
}

export interface IGetAvatarChange extends IGetRequest {
  avatars_report: Avatar[];
  background: Background;
  foreground: Background;
  audio: Audio;
}
export interface IGetKeysProgress {
  has_animation: boolean;
  animation_file_link: string;
}

export interface StarLevel {
  has_animation: boolean;
  animation_file_link: string;
}

export interface StarReport {
  type: string;
  levels: {
    [level: string]: StarLevel;
  };
}

export interface AnimationAudio {
  exists: boolean;
  url: string;
}

export interface ILevelUp {
  avatars_report: Avatar[];
  stars_report: {
    Bronze: StarReport;
    Silver: StarReport;
    Gold: StarReport;
    Platinum: StarReport;
    Emerald: StarReport;
    Diamond: StarReport;
    Ruby: StarReport;
  };
  animation_audio_links: {
    level_1: AnimationAudio;
    level_2: AnimationAudio;
    level_3: AnimationAudio;
  };
}

export interface IGetLevelUp extends IGetRequest {
  result: ILevelUp;
}